import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { getOrdersByOrderNumber } from '../modules/orderdata';
import OrderItem from './orderitem';
import OrderConfirmationMailInputDialog from './orderConfirmationMailInfoInputDialog';
import OrderSendStatusDialog from './ordersendstatusdialog';
import Footer from '../main/footer';
import FooterPadding from '../main/footerpadding';
import { Alert, CircularProgress, Grid, Button } from '@mui/material';
import PublishIcon from '@mui/icons-material/Publish';
import CancelIcon from '@mui/icons-material/Cancel';
import { cloneDeep, find, maxBy, some } from 'lodash';
import { sendEditOrders } from '../modules/orderdata';
import { removeCapacityReservations } from './capacityplannerproducttypes';
import { getEarliestDeliveryDateWithPayload } from '../modules/prodplanningdata';
import OrderRow from '../domain/orderrow';
import FilterProduct from '../domain/filterproduct';
import { ProductBundle } from '../domain/productbundle';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Order from '../domain/order';
import dayjs from 'dayjs';

export default function OrderEditViewContainer(props) {
    const dispatch = useDispatch();
    const ordersByOrderNumber = useSelector((state) => state.orderdata.ordersByOrderNumber);
    const customersById = useSelector((state) => state.customerdata.customersById);
    const customerDescriptions = useSelector((state) => state.customerdata.customerDescriptions);
    const sendingOrdersInProgress = useSelector((state) => state.orderdata.sendingOrdersInProgress);
    const savingCombinedOrder = useSelector((state) => state.orderdata.savingCombinedOrder);
    const orderSendFailed = useSelector((state) => state.orderdata.orderSendFailed);
    const parameters = useSelector((state) => state.parameterdata.parameters);
    const products = useSelector((state) => state.productdata.filterProducts);
    const bundles = useSelector((state) => state.productdata.productBundles);
    const orderNumber = parseInt(props.match.params.orderNumber, 10);
    const [ordersToEdit, setOrdersToEdit] = useState(null);
    const [orderFormOk, setOrderFormOk] = useState(false);
    const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
    const [sendingOrdersModalOpen, setSendingOrdersModalOpen] = useState(false);
    const [earliestDeliveryDate, setEarliestDeliveryDate] = useState(null);
    const [earliestDeliveryDateAvailable, setEarliestDeliveryDateAvailable] = useState(false);
    const [earliestDeliveryDateDetails, setEarliestDeliveryDateDetails] = useState(null);
    const portfolios = useSelector((state) => state.portfoliodata.portfolioDescriptions);
    const [portfolio, setPortfolio] = useState({});
    const allowPricing = true;
    const store = useStore();
    const ordersToEditRef = useRef(null);
    const { t, i18n } = useTranslation();
    const userLanguage = i18n.language;
    const deliveryDateInfosForOrders = useSelector((state) => state.prodplanningdata.deliveryDateInfosForOrders);
    const loadingDeliveryDateInfos = useSelector((state) => state.prodplanningdata.loadingDeliveryDateInfos);
    const loadingDeliveryDateInfosRef = useRef(false);
    const timerHandle = useRef(null);
    const needToUpdateCapaRef = useRef(false);

    useEffect(() => {
        ordersToEditRef.current = ordersToEdit;
    }, [ordersToEdit]);

    useEffect(() => {
        return () => {
            if (timerHandle.current) {
                clearTimeout(timerHandle.current);
            }
        };
    }, []);

    useEffect(() => {
        loadingDeliveryDateInfosRef.current = loadingDeliveryDateInfos;
    }, [loadingDeliveryDateInfos]);

    useEffect(() => {
        if (!ordersByOrderNumber[orderNumber]) {
            dispatch(getOrdersByOrderNumber(orderNumber));
        } else {
            setOrdersToEdit(cloneDeep(ordersByOrderNumber[orderNumber]));
        }
    }, [ordersByOrderNumber, dispatch, orderNumber]);

    useEffect(() => {
        if (!ordersToEdit || ordersToEdit.length === 0) return;

        if (!deliveryDateInfosForOrders[ordersToEdit[0].id] && !loadingDeliveryDateInfos) {
            getEarliestDeliveryDateWithPayload(ordersToEdit)(dispatch);
        } else if (!loadingDeliveryDateInfos) {
            if (
                some(deliveryDateInfosForOrders[ordersToEdit[0].id], (t) => {
                    return t.earliestDeliveryDate == null;
                })
            ) {
                setEarliestDeliveryDate(null);
                setEarliestDeliveryDateDetails(deliveryDateInfosForOrders[ordersToEdit[0].id]);
            } else {
                setEarliestDeliveryDate(
                    maxBy(deliveryDateInfosForOrders[ordersToEdit[0].id], (t) =>
                        dayjs(t.earliestDeliveryDate).valueOf()
                    ).earliestDeliveryDate
                );
                setEarliestDeliveryDateDetails(deliveryDateInfosForOrders[ordersToEdit[0].id]);
            }
            setEarliestDeliveryDateAvailable(true);
        }
    }, [ordersToEdit, deliveryDateInfosForOrders, dispatch, loadingDeliveryDateInfos]);

    useEffect(() => {
        if (ordersByOrderNumber[orderNumber] && portfolios && portfolios.length > 0) {
            setPortfolio(portfolios.find((p) => p.id === ordersByOrderNumber[orderNumber][0].portfolioId));
        }
    }, [ordersByOrderNumber, portfolios, orderNumber]);

    const removeOrder = (orderToRemoveId) => {
        const newOrdersToEdit = ordersToEditRef.current.filter((o) => o.id !== orderToRemoveId);
        setOrdersToEdit(newOrdersToEdit);
    };

    const updateEarliestDeliveryDateIfNeeded = (newOrder) => {
        // we have established that we need to update and timer is running already for timed update, leave
        // it as is and let it update.
        if (timerHandle.current && needToUpdateCapaRef.current) {
            return;
        }

        let needToUpdate = false;
        const prevOrder = ordersToEditRef.current.find((o) => o.id === newOrder.id);
        for (let row of prevOrder.orderedFilters) {
            const newRow = newOrder.orderedFilters.find((r) => r.id === row.id);

            // Row removed, update
            if (!newRow) {
                needToUpdate = true;
                break;
            }
            if (newRow.count !== row.count) {
                needToUpdate = true;
                break;
            }
        }
        if (!needToUpdate) {
            for (let row of newOrder.orderedFilters) {
                const oldRow = prevOrder.orderedFilters.find((r) => r.id === row.id);

                // Row addded, update
                if (!oldRow) {
                    needToUpdate = true;
                    break;
                }
            }
        }

        if (!needToUpdate && prevOrder.orderType !== newOrder.orderType) {
            needToUpdate = true;
        }

        // we need update and no update is running now
        if (needToUpdate) {
            needToUpdateCapaRef.current = true;
            timerHandle.current = setTimeout(() => {
                triggerDeliveryDateUpdate();
            }, 2000);
        }
    };

    const triggerDeliveryDateUpdate = () => {
        timerHandle.current = null;
        needToUpdateCapaRef.current = false;
        // update is pending towards backend. Retrigger timer
        if (loadingDeliveryDateInfosRef.current) {
            needToUpdateCapaRef.current = true;
            timerHandle.current = setTimeout(() => {
                triggerDeliveryDateUpdate();
            }, 2000);
        } else {
            getEarliestDeliveryDateWithPayload(ordersToEditRef.current)(dispatch);
        }
    };

    const orderChanged = (newOrder) => {
        const newOrdersToEdit = ordersToEditRef.current.map((o) => {
            const ord = new Order(o);
            if (ord.id === newOrder.id) ord.orderedFilters = newOrder.orderedFilters;
            ord.deliveryTerms = newOrder.deliveryTerms;
            ord.deliveryTime = newOrder.deliveryTime;
            ord.ordererName = newOrder.ordererName;
            ord.ordererTel = newOrder.ordererTel;
            ord.reference = newOrder.reference;
            ord.confirmationEmail = newOrder.confirmationEmail;
            ord.deliveryConfirmationEmail = newOrder.deliveryConfirmationEmail;
            ord.packingInstructions = newOrder.packingInstructions;
            ord.orderType = newOrder.orderType;
            ord.generalInfo = newOrder.generalInfo;
            ord.deliveryMethod = newOrder.deliveryMethod;
            ord.earlyDeliveryTime = newOrder.earlyDeliveryTime;
            ord.updateTotalPrice();
            ord.billingAddress = newOrder.billingAddress;
            ord.electronicInvoiceAddress = newOrder.electronicInvoiceAddress;

            //case for address change - update for all (only in case it is not private customer order)
            if (!ord.orderToPrivateCustomer) {
                ord.deliveryAddress = newOrder.deliveryAddress;
                ord.deliveryRecipient = newOrder.deliveryRecipient;
            }
            return ord;
        });
        updateEarliestDeliveryDateIfNeeded(newOrder);
        setOrdersToEdit(newOrdersToEdit);
    };

    const updateOrderFormState = (isFormOk, orderId) => {
        setOrderFormOk(isFormOk);
    };

    const onReserveCapacity = async (selectedDate, order) => {
        return true;
    };

    const onOrderConfirmationMailInfo = () => {
        setConfirmationDialogOpen(true);
    };

    const onConfirmationCancel = () => {
        setConfirmationDialogOpen(false);
    };

    const onConfirmationAccept = async (language, confirmationOptions) => {
        await onSendOrders(confirmationOptions, language);
        props.history.goBack();
    };

    const onSendOrders = async (confirmationOptions, language) => {
        setSendingOrdersModalOpen(true);
        setConfirmationDialogOpen(false);
        await sendEditOrders(ordersToEdit, confirmationOptions, language, [])(dispatch);
    };

    const isOrderSendDone = () => {
        return !sendingOrdersInProgress && !savingCombinedOrder;
    };

    const okBtn = () => {
        setSendingOrdersModalOpen(false);
    };

    const onCancel = () => {
        props.history.goBack();
    };

    const addProductWithId = (orderId, productId) => {
        let product = find(products, { id: productId });
        if (!product) product = find(bundles, { id: productId });

        if (!product) {
            toast.error(t('order.errorProductNotFound', { prod: productId }), { autoClose: 5000 });
            return;
        }
        addProductInternal(orderId, product);
    };

    const addProductWithCode = (orderId, productCode) => {
        const product = find(products, { productCode: productCode });
        console.log(product);
        if (!product) {
            toast.error(t('order.errorProductNotFound', { prod: productCode }), { autoClose: 5000 });
            return;
        }
        addProductInternal(orderId, product);
    };

    const addProductInternal = (orderId, product) => {
        const prevOrder = ordersToEditRef.current.find((o) => o.id === orderId);
        const newOrder = new Order(prevOrder);

        OrderRow.NewObject().then((ford) => {
            ford.storageId = null;
            ford.product = product.productType() === 'fp' ? new FilterProduct(product) : null;
            ford.productBundle = product.productType() === 'bundle' ? new ProductBundle(product) : null;
            ford.machineName = '';
            ford.machineLocation = '';
            ford.machineArea = '';
            ford.machineAdditionalInfo = '';
            ford.count = 1;
            ford.discount = 0;
            ford.totalPriceWithoutVat =
                product.productType() === 'fp' ? product.priceWithoutVat : product.priceWithoutVat();
            ford.unitPriceWithoutVat =
                product.productType() === 'fp' ? product.priceWithoutVat : product.priceWithoutVat();
            ford.viewOrder = newOrder.orderedFilters.length;
            ford.machineViewOrder = maxBy(newOrder.orderedFilters, (f) => f.machineViewOrder).machineViewOrder + 1;
            ford.palletInformation = '';
            ford.filterAdditionalInfo = '';

            const newOrdersToEdit = ordersToEditRef.current.map((o) => {
                if (o.id === newOrder.id) {
                    newOrder.orderedFilters.push(ford);
                    return newOrder;
                }
                return o;
            });
            updateEarliestDeliveryDateIfNeeded(newOrder);
            setOrdersToEdit(newOrdersToEdit);
        });
    };

    const onRemoveCapacityReservations = async (orderId) => {
        await removeCapacityReservations(store, orderId);
    };

    if (!ordersToEdit) {
        return (
            <div>
                {t('general.loading')} <CircularProgress color={'secondary'} size={'1rem'} />
            </div>
        );
    }

    return (
        <div>
            <Alert severity="warning">{t('order.youAreEditingSentOrder')}</Alert>
            <OrderItem
                customerView={false}
                key={ordersToEdit[0].id}
                orders={ordersToEdit}
                removeOrder={removeOrder}
                updateOrderFormState={updateOrderFormState}
                allowPricing={allowPricing}
                customers={customerDescriptions ? customerDescriptions : []}
                customersById={customersById}
                makeCapacityReservations={onReserveCapacity}
                earliestDeliveryDate={earliestDeliveryDate}
                earliestDeliveryDateAvailable={earliestDeliveryDateAvailable}
                earliestDeliveryDateDetails={earliestDeliveryDateDetails}
                editOrderedItem={true}
                parameters={parameters}
                editFoPalletInfo={() => {}}
                portfolio={portfolio}
                addProductWithCode={addProductWithCode}
                addProductWithId={addProductWithId}
                removeCapacityReservations={onRemoveCapacityReservations}
                orderChanged={orderChanged}
            />
            <OrderConfirmationMailInputDialog
                open={confirmationDialogOpen}
                cancel={onConfirmationCancel}
                save={onConfirmationAccept}
                orders={ordersToEdit}
                combineOrders={ordersToEdit.length > 1}
                userLanguage={userLanguage}
            />
            <OrderSendStatusDialog
                open={sendingOrdersModalOpen}
                orderSendDone={isOrderSendDone()}
                orderSendFailed={orderSendFailed}
                okBtn={okBtn}
            />
            <Footer>
                <Grid item>
                    <Button
                        variant="contained"
                        color="primary"
                        id="send-order-edit-btn"
                        name="send-order-edit-btn"
                        disabled={!orderFormOk}
                        onClick={onOrderConfirmationMailInfo}
                        startIcon={<PublishIcon />}>
                        {t('buttons.send')}
                    </Button>
                </Grid>
                <Grid item>
                    <Button
                        variant="contained"
                        color="primary"
                        name="mass-edit-btn"
                        onClick={onCancel}
                        startIcon={<CancelIcon />}>
                        {t('buttons.cancel')}
                    </Button>
                </Grid>
            </Footer>
            <FooterPadding />
        </div>
    );
}
